import type { ComponentType } from "react";
import CompetitionWidget from "@/components/widgets/CompetitionWidget";
import ResearchWidget from "@/components/widgets/ResearchWidget";
import PressWidget from "@/components/widgets/PressWidget";
import ExemplarsWidget from "@/components/widgets/ExemplarsWidget";
import MarketActivityWidget from "@/components/widgets/MarketActivityWidget";
import OutreachWidget from "@/components/widgets/OutreachWidget";
import ValueAddWidget from "@/components/widgets/ValueAddWidget";
import EOIWidget from "@/components/widgets/EOIWidget";
import DirectoryWidget from "@/components/widgets/DirectoryWidget";
import InvestorReportsWidget from "@/components/widgets/InvestorReportsWidget";
import SitePipelineWidget from "@/components/widgets/SitePipelineWidget";
import IntroVideoWidget from "@/components/widgets/IntroVideoWidget";
import MediaGalleryWidget from "@/components/widgets/MediaGalleryWidget";

export interface WidgetConfig {
  id: string;
  title: string;
  component: ComponentType<{ projectId: string }>;
  area: "main" | "sidebar";
  collapsible: boolean;
  headerColor?:
    | "purple"
    | "green"
    | "orange"
    | "blue"
    | "pink"
    | "yellow"
    | "indigo"
    | "red";
  visibilityKey: keyof SectionVisibility;
  order: number;
}

export interface SectionVisibility {
  show_documents: boolean;
  show_site_pipeline: boolean;
  show_media_gallery: boolean;
  show_competition: boolean;
  show_research: boolean;
  show_press: boolean;
  show_market_activity?: boolean;
  show_metrics: boolean;
  show_funding: boolean; // ADD THIS LINE
  show_admin_note: boolean;
  show_action_buttons: boolean;
  show_intro_video: boolean;
  show_directory: boolean;
  show_outreach: boolean;
  show_value_add: boolean;
  show_eoi: boolean;
  show_investor_reports: boolean;
  show_exemplars?: boolean;
}

export const WIDGET_REGISTRY: WidgetConfig[] = [
  // Main Content Widgets
  {
    id: "site_pipeline",
    title: "Site Pipeline",
    component: SitePipelineWidget,
    area: "main",
    collapsible: false,
    headerColor: "indigo",
    visibilityKey: "show_site_pipeline",
    order: 1,
  },
  {
    id: "media_gallery", // PHASE 2
    title: "Media Gallery",
    component: MediaGalleryWidget,
    area: "main",
    collapsible: false,
    headerColor: "blue",
    visibilityKey: "show_media_gallery",
    order: 2,
  },
  {
    id: "competition",
    title: "Competition Overview",
    component: CompetitionWidget,
    area: "main",
    collapsible: true,
    headerColor: "purple",
    visibilityKey: "show_competition",
    order: 3,
  },
  {
    id: "exemplars",
    title: "Exemplars",
    component: ExemplarsWidget,
    area: "main",
    collapsible: true,
    headerColor: "yellow",
    order: 4,
    visibilityKey: "show_exemplars",
  },
  {
    id: "research",
    title: "Research Library",
    component: ResearchWidget,
    area: "main",
    collapsible: true,
    headerColor: "green",
    visibilityKey: "show_research",
    order: 5,
  },
  {
    id: "press",
    title: "Press Coverage",
    component: PressWidget,
    area: "main",
    collapsible: true,
    headerColor: "orange",
    visibilityKey: "show_press",
    order: 6,
  },
  {
    id: "market_activity",
    title: "Market Activity",
    component: MarketActivityWidget,
    area: "main",
    collapsible: true,
    headerColor: "blue",
    visibilityKey: "show_market_activity",
    order: 7,
  },

  // Sidebar Widgets (Not Collapsible)
  {
    id: "intro_video", // PHASE 1
    title: "Introduction",
    component: IntroVideoWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_intro_video",
    order: 0,
  },
  {
    id: "directory",
    title: "Directory",
    component: DirectoryWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_directory",
    order: 1,
  },
  {
    id: "outreach",
    title: "Current Outreach",
    component: OutreachWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_outreach",
    order: 2,
  },
  {
    id: "value_add",
    title: "Value Add Support",
    component: ValueAddWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_value_add",
    order: 3,
  },
  {
    id: "eoi",
    title: "Expression of Interest",
    component: EOIWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_eoi",
    order: 4,
  },
  {
    id: "investor_reports",
    title: "Investor Reports",
    component: InvestorReportsWidget,
    area: "sidebar",
    collapsible: false,
    visibilityKey: "show_investor_reports",
    order: 5,
  },
];

export function getMainWidgets(): WidgetConfig[] {
  return WIDGET_REGISTRY.filter((w) => w.area === "main").sort(
    (a, b) => a.order - b.order
  );
}

export function getSidebarWidgets(): WidgetConfig[] {
  return WIDGET_REGISTRY.filter((w) => w.area === "sidebar").sort(
    (a, b) => a.order - b.order
  );
}
